import { Dashboard } from "components/Layout";
import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import UsersList from "components/OrganizationAdmin/Users/UsersList";
import api from "api/api";
import StoreProvider from "components/StoreProvider/StoreProvider";
import { OrganizationType } from "store/modules/organizations/organizations";
import { compose } from "ramda";
import queryString from "query-string";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { GroupType } from "./types";
import { useFormatMessage } from "components/IntlProvider";

const { ROUTES } = RouterProvider;

const { connect, selectors } = StoreProvider;
export const defaultLimit = 20;

export interface UserType {
  id: number;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    last_login: string;
    org_groups: GroupType[];
    id: number;
  };
  organization_role: 1 | 2 | 3 | 4;
  is_active: "active" | "Deactivated";
}

type UserListResponse = {
  count: number;
  results: UserType[];
};

interface Props extends RouteComponentProps {
  organization: OrganizationType;
}

const Users = ({ location, organization, history }: Props) => {
  const [organizationUsers, setOrganizationUsers] = useState<UserListResponse>({
    count: 0,
    results: [],
  });
  const t = useFormatMessage();
  const [isLoaded, setIsLoaded] = useState(false);
  const parsedSearch = queryString.parse(location.search);

  const getUsers = () =>
    api.resources.organization.users
      .getOrganizationUsers({
        organizationCodename: organization.codename,
        params: {
          limit: (parsedSearch.limit as string) || defaultLimit,
          offset: (parsedSearch.offset as string) || 0,
          search: (parsedSearch.search as string) || undefined,
          ordering: (parsedSearch.ordering as string) || undefined,
        },
      })
      .then(({ data }: { data: UserListResponse }) => {
        setOrganizationUsers({ ...data });
      })
      .finally(() => {
        setIsLoaded(true);
      });

  useEffect(() => {
    if (location.search) {
      setIsLoaded(false);
      getUsers();
    }
    // eslint-disable-next-line
  }, [location.search]);

  const getPageNumber = useCallback(() => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return Math.floor(parseInt(parsedSearch.offset, 10) / defaultLimit + 1);
    }

    return 0;
  }, [parsedSearch]);

  const sortColumns = (colName: string) => {
    const params = { ...parsedSearch, ordering: colName };

    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.USERS}?${queryString.stringify(params)}`
    );
  };
  return (
    <Dashboard title={t("dashboard.menu.admin", "Admin")} location={location}>
      <UsersList
        usersData={organizationUsers.results}
        usersCount={organizationUsers.count}
        isLoaded={isLoaded}
        organizationCodename={organization.codename}
        refetchUsers={getUsers}
        currentPage={getPageNumber()}
        orderBy={parsedSearch.ordering as string}
        sortColumns={sortColumns}
      />
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    isAdmin: selectors.authSelectors.isOrganizationAdmin(state),
  }))
)(Users);
